// Pure pricing logic for POS transactions.
//
// All functions here are side-effect-free and synchronous — they can be called
// in React render, inside cart totals, or in transaction tests without any
// mocking. Everything is integer IDR; we never introduce floating point math
// inside the cart pipeline.
//
// Mental-test cases (all pass):
//   subtotal 100_000
//   discount 10%  → discountAmount 10_000, newSubtotal 90_000
//   tax      11%  on 90_000 → taxAmount 9_900
//   grandTotal    = 90_000 + 9_900 = 99_900
//
//   cash paid 100_000, grandTotal 99_900 → change 100
//   qris/transfer paid = grandTotal, change = 0

import type { DiscountType, PaymentMethod, PromoKind } from '@/types/domain';

/** A line item in the cart — denormalized at add-time for stability. */
export interface PricingCartItem {
  productId: string;
  name: string;
  price: number;
  qty: number;
  /** Maximum stock the cart should allow for this product. Optional. */
  maxStock?: number;
}

/** Sum of (price * qty) for every line item. */
export function calculateItemSubtotal(items: ReadonlyArray<PricingCartItem>): number {
  let subtotal = 0;
  for (const item of items) {
    if (!Number.isFinite(item.price) || !Number.isFinite(item.qty)) continue;
    if (item.qty <= 0) continue;
    subtotal += Math.round(item.price * item.qty);
  }
  return subtotal;
}

export interface DiscountResult {
  discountAmount: number;
  newSubtotal: number;
}

/**
 * Compute discount on a (pre-discount) subtotal.
 * - `type='percent'` and `value=10` means 10% off.
 * - `type='nominal'` and `value=5000` means flat Rp 5.000 off.
 * - `type=null` or value<=0 → no discount.
 * - Discount can never exceed the subtotal (clamped to subtotal).
 */
export function calculateDiscount(
  subtotal: number,
  type: DiscountType,
  value: number,
): DiscountResult {
  if (subtotal <= 0 || !type || !Number.isFinite(value) || value <= 0) {
    return { discountAmount: 0, newSubtotal: Math.max(0, subtotal) };
  }
  let discountAmount = 0;
  if (type === 'percent') {
    // Cap percent at 100 to avoid negative newSubtotal.
    const pct = Math.min(100, value);
    discountAmount = Math.round((subtotal * pct) / 100);
  } else if (type === 'nominal') {
    discountAmount = Math.round(value);
  }
  if (discountAmount > subtotal) discountAmount = subtotal;
  if (discountAmount < 0) discountAmount = 0;
  return { discountAmount, newSubtotal: subtotal - discountAmount };
}

export interface TaxResult {
  taxAmount: number;
  total: number;
}

/**
 * Apply tax (PPN) to a (post-discount) base.
 * - `enabled=false` → taxAmount 0, total = base unchanged.
 * - `rate=11` means 11% — caller is responsible for passing rate as a
 *   percentage (e.g. 11, not 0.11).
 */
export function calculateTax(
  base: number,
  rate: number,
  enabled: boolean,
): TaxResult {
  const safeBase = Math.max(0, Math.round(base));
  if (!enabled || !Number.isFinite(rate) || rate <= 0) {
    return { taxAmount: 0, total: safeBase };
  }
  const taxAmount = Math.round((safeBase * rate) / 100);
  return { taxAmount, total: safeBase + taxAmount };
}

// ── Promos ──────────────────────────────────────────────────────────────────
// A mirror of the promo pass in create_transaction() (migration 0037). Same
// inputs, same order, same rounding — so the number the cashier sees is the
// number the invoice gets. The server remains the authority: it refuses a
// promo that does not apply, and this preview reports the same refusal as a
// `problem` string instead of throwing.
//
// Money order: line promos (bundle, bogo) in the order chosen → the double
// discount on what is left → the cashier's manual discount → PPN.

export interface PromoLine {
  /** Stable key for reporting the per-line discount back (allocation id). */
  key: string;
  productId: string;
  price: number;
  qty: number;
}

export interface PromoDef {
  id: string;
  kind: PromoKind;
  name: string;
  config: Record<string, unknown>;
  items: { productId: string; qty: number; buyQty?: number; getQty?: number }[];
}

export interface PromoApplied {
  promoId: string;
  kind: PromoKind;
  name: string;
  amount: number;
  times?: number;
  free?: number;
  steps?: number[];
}

export interface PromoResult {
  /** Σ of every promo — what transactions.promo_discount will hold. */
  promoDiscount: number;
  applied: PromoApplied[];
  /** Per line key → promo money taken off that line. */
  lineDiscount: Map<string, number>;
  /** First refusal the server would raise, or null. */
  problem: string | null;
}

const num = (v: unknown, fallback = 0): number =>
  typeof v === 'number' && Number.isFinite(v) ? v : Number(v ?? fallback) || fallback;

/** Apply promos to ordered lines exactly as the database does. */
export function applyPromos(lines: ReadonlyArray<PromoLine>, promos: ReadonlyArray<PromoDef>): PromoResult {
  const lineDiscount = new Map<string, number>();
  const sub = new Map<string, number>();
  for (const l of lines) {
    lineDiscount.set(l.key, 0);
    sub.set(l.key, Math.round(l.price * l.qty));
  }
  const applied: PromoApplied[] = [];
  let total = 0;
  let double: PromoDef | null = null;
  const seen = new Set<string>();

  const qtyOf = (productId: string) =>
    lines.filter((l) => l.productId === productId).reduce((n, l) => n + l.qty, 0);

  for (const p of promos) {
    if (seen.has(p.id)) return fail(`Promo "${p.name}" dipakai dua kali`);
    seen.add(p.id);

    if (p.kind === 'bundle') {
      if (p.items.length === 0) return fail(`Bundle "${p.name}" belum punya isi`);
      let times = Infinity;
      for (const c of p.items) times = Math.min(times, Math.floor(qtyOf(c.productId) / Math.max(1, c.qty)));
      if (!Number.isFinite(times) || times < 1) return fail(`Bundle "${p.name}" belum lengkap di keranjang`);
      // Units the bundle consumes, per line in order; gross of those units.
      const take: { key: string; gross: number }[] = [];
      let gross = 0;
      for (const c of p.items) {
        let units = c.qty * times;
        for (const l of lines) {
          if (units <= 0) break;
          if (l.productId !== c.productId) continue;
          const t = Math.min(units, l.qty);
          take.push({ key: l.key, gross: t * l.price });
          gross += t * l.price;
          units -= t;
        }
      }
      const amount = gross - Math.round(num(p.config.bundle_price)) * times;
      if (amount <= 0) return fail(`Bundle "${p.name}" tidak lebih murah dari harga satuan`);
      let given = 0;
      take.forEach((t, i) => {
        const share = i === take.length - 1 ? amount - given : Math.round((amount * t.gross) / gross);
        lineDiscount.set(t.key, (lineDiscount.get(t.key) ?? 0) + share);
        given += share;
      });
      total += amount;
      applied.push({ promoId: p.id, kind: 'bundle', name: p.name, amount, times });
    } else if (p.kind === 'bogo') {
      // Each product carries its own rule (migration 0039); the promo-level
      // config is only the fallback the database also uses.
      let amount = 0;
      let free = 0;
      for (const c of p.items) {
        const buy = Math.floor(c.buyQty ?? num(p.config.buy_qty, 0));
        const get = Math.floor(c.getQty ?? num(p.config.get_qty, 0));
        if (!(buy >= 1) || !(get >= 1)) return fail(`Promo "${p.name}" belum lengkap: atur beli dan gratis untuk setiap produk`);
        let take = Math.floor(qtyOf(c.productId) / (buy + get)) * get;
        free += take;
        for (const l of lines) {
          if (take <= 0) break;
          if (l.productId !== c.productId) continue;
          const share = Math.min(take, l.qty);
          lineDiscount.set(l.key, (lineDiscount.get(l.key) ?? 0) + share * l.price);
          amount += share * l.price;
          take -= share;
        }
      }
      if (free < 1) return fail(`Promo "${p.name}" belum berlaku: jumlah di keranjang belum mencapai syarat beli-gratis`);
      total += amount;
      applied.push({ promoId: p.id, kind: 'bogo', name: p.name, amount, free });
    } else if (p.kind === 'double_discount') {
      if (double) return fail('Hanya satu diskon bertingkat per transaksi');
      double = p;
      applied.push({ promoId: p.id, kind: 'double_discount', name: p.name, amount: 0,
        steps: Array.isArray(p.config.steps) ? p.config.steps.map((x) => num(x)) : [] });
    }
  }

  for (const l of lines) {
    if ((lineDiscount.get(l.key) ?? 0) > (sub.get(l.key) ?? 0)) return fail('Promo melebihi nilai baris');
  }

  if (double) {
    const d: PromoDef = double;
    let base = 0;
    for (const l of lines) base += (sub.get(l.key) ?? 0) - (lineDiscount.get(l.key) ?? 0);
    const steps = Array.isArray(d.config.steps) ? d.config.steps.map((x) => num(x)) : [];
    let factor = 1;
    for (const st of steps) factor *= 1 - st / 100;
    const amount = Math.round(base * (1 - factor));
    total += amount;
    const entry = applied.find((a) => a.promoId === d.id);
    if (entry) entry.amount = amount;
  }

  return { promoDiscount: total, applied, lineDiscount, problem: null };

  function fail(problem: string): PromoResult {
    return { promoDiscount: 0, applied: [], lineDiscount: new Map(), problem };
  }
}

/**
 * Which promos a sale carries.
 *
 * Buy-N-get-M is attached to the product, not switched on by the cashier: it
 * applies whenever the cart reaches it, and quietly does not when it does not.
 * Bundles and the tiered discount stay the cashier's choice (`chosenIds`).
 * Bogo goes first so a bundle chosen on top sees what is already free.
 * The resulting ids are exactly what is sent to create_transaction.
 */
export function resolveAutoPromos(
  lines: ReadonlyArray<PromoLine>,
  activePromos: ReadonlyArray<PromoDef>,
  chosenIds: ReadonlyArray<string>,
): PromoDef[] {
  const auto = activePromos.filter((p) => p.kind === 'bogo' && !applyPromos(lines, [p]).problem);
  const chosen = chosenIds
    .map((id) => activePromos.find((p) => p.id === id && p.kind !== 'bogo'))
    .filter((p): p is PromoDef => !!p);
  return [...auto, ...chosen];
}

export interface TotalsInput {
  items: ReadonlyArray<PricingCartItem>;
  discountType: DiscountType;
  discountValue: number;
  taxEnabled: boolean;
  taxRate: number;
  /** Ordered lines and promos for the promo pass. Omit for none. */
  promoLines?: ReadonlyArray<PromoLine>;
  promos?: ReadonlyArray<PromoDef>;
}

export interface Totals {
  subtotal: number;
  promoDiscount: number;
  appliedPromos: PromoApplied[];
  /** Per line key (allocation id) → promo money off that line. */
  promoByLine: Map<string, number>;
  promoProblem: string | null;
  discountAmount: number;
  taxAmount: number;
  grandTotal: number;
}

/** One-shot total calculator used by the cart and the checkout summary. */
export function calculateTotals(input: TotalsInput): Totals {
  const subtotal = calculateItemSubtotal(input.items);
  const promo =
    input.promos && input.promos.length > 0 && input.promoLines
      ? applyPromos(input.promoLines, input.promos)
      : { promoDiscount: 0, applied: [], lineDiscount: new Map<string, number>(), problem: null };
  const base = subtotal - promo.promoDiscount;
  const { discountAmount } = calculateDiscount(
    base,
    input.discountType,
    input.discountValue,
  );
  const taxableBase = base - discountAmount;
  const { taxAmount, total: grandTotal } = calculateTax(
    taxableBase,
    input.taxRate,
    input.taxEnabled,
  );
  return {
    subtotal,
    promoDiscount: promo.promoDiscount,
    appliedPromos: promo.applied,
    promoByLine: promo.lineDiscount,
    promoProblem: promo.problem,
    discountAmount,
    taxAmount,
    grandTotal,
  };
}

export interface ChangeResult {
  paidAmount: number;
  change: number;
}

/**
 * Compute change for a given payment.
 * - For `cash`: paidAmount is what the customer handed over; change = paidAmount - grandTotal.
 *   Caller is responsible for not allowing paidAmount < grandTotal — but if
 *   the caller accidentally under-pays, change will be negative.
 * - For `qris`/`transfer`: paidAmount is forced to grandTotal, change = 0.
 */
export function calculateChange(
  grandTotal: number,
  method: PaymentMethod,
  paidAmount: number,
): ChangeResult {
  if (method === 'cash') {
    const paid = Math.max(0, Math.round(paidAmount));
    return { paidAmount: paid, change: paid - grandTotal };
  }
  return { paidAmount: grandTotal, change: 0 };
}
